
import { Component, Vue } from 'vue-property-decorator';

import axios, { AxiosResponse } from 'axios';

import CreateEditUserModal from '@/components/CreateEditUserModal.vue';
import { ApplicationModule, ApplicationModuleName, Auth0User, Company, User, UserGroup, UserRole, UserRoleName } from '@/lib/models';

@Component({
  components: {
    CreateEditUserModal,
  },
})
export default class Users extends Vue {
  private company: Company | null = null;
  private readonly hasTraceAdminRole: boolean = this.hasUserRole(UserRoleName.TraceAdmin);
  private readonly hasSupportAccessRole: boolean = this.hasUserRole(UserRoleName.SupportAccess);
  private readonly hasCompanyAdminRole: boolean = this.hasUserRole(UserRoleName.CompanyAdmin);
  private submitting: boolean = false;

  private isLoading: boolean = false;
  private userGroups: UserGroup[] = [];
  private userRoles: UserRole[] = [];
  private applicationModules: ApplicationModule[] = [];
  private userToEdit: User | null = null;

  private get userGroupIdToNameMap(): Map<string, string> {
    const result = new Map();
    for (const userGroup of this.userGroups) {
      result.set(userGroup.id, userGroup.name);
    }
    return result;
  }

  private get userRoleIdToNameMap(): Map<string, string> {
    const result = new Map();
    for (const userRole of this.userRoles) {
      result.set(userRole.id, userRole.name);
    }
    return result;
  }

  private userSyncToolDownloadUrl: string = process.env.VUE_APP_USER_SYNC_TOOL_DOWNLOAD_URL;

  private currentPage: number = 1;
  private totalRows: number = 0;
  private pageOptions: number[] = [5, 10, 15, 20];
  private perPage: number = 10;

  private userFields: any[] = [
    {
      key: 'email',
      sortable: true,
    },
    {
      key: 'given_name',
      sortable: true,
      label: 'First Name',
    },
    {
      key: 'family_name',
      sortable: true,
      label: 'Last Name',
    },
    {
      key: 'app_metadata.title',
      sortable: true,
      label: 'Title',
    },
    {
      key: 'actions',
      sortable: false,
    },
  ];

  private readonly activeOptions: string[] = ['active', 'inactive', 'all'];

  private userFilter: any = {
    active: 'active',
    email: '',
    firstName: '',
    lastName: '',
  };

  private hasUserRole(name: UserRoleName): boolean {
    return (this as any).$auth0.user[process.env.VUE_APP_AUTH0_CUSTOM_CLAIM_NAMESPACE + '/roles'].includes(name);
  }

  private async userProvider(context: any): Promise<Auth0User[]> {
    // Auth0 page numbering starts at 0, so substract from currentPage
    let url: string = `${process.env.VUE_APP_BACKEND_API_HOST}/new_users?page=${context.currentPage - 1}&per_page=${context.perPage}`;

    if (context.sortBy !== '') {
      url += `&sort=${context.sortBy}:${context.sortDesc ? 'desc' : 'asc'}`;
    }

    if (context.filter.active !== '') {
      if (context.filter.active === 'active') {
        url += '&active=true';
      } else if (context.filter.active === 'inactive') {
        url += '&active=false';
      }
    }

    if (context.filter.email !== '') {
      url += `&email=${encodeURIComponent(context.filter.email)}`;
    }

    if (context.filter.firstName !== '') {
      url += `&given_name=${encodeURIComponent(context.filter.firstName)}`;
    }

    if (context.filter.lastName !== '') {
      url += `&family_name=${encodeURIComponent(context.filter.lastName)}`;
    }

    const accessToken: string = await (this as any).$auth0.getAccessTokenSilently();
    this.isLoading = true;
    return axios.get(url, {
      headers: {
        Authorization: `Bearer ${accessToken}`,
      },
    }).then((response) => {
      const users = response.data.items;
      for (const user of users) {
        user._showDetails = false;
      }
      this.totalRows = response.data.total;
      return users || [];
    }).catch((error) => {
      const message: string = error.response?.data?.message ?? error.message;
      this.$bvModal.msgBoxOk(message, {
        title: 'Error Getting Users',
        okVariant: 'danger',
        centered: true,
      }).then(() => {
        return [];
      });
    }).finally(() => {
      this.isLoading = false;
    });
  }

  private get userEmail(): string {
    return (this as any).$auth0.user.email;
  }

  private created(): void {
    Promise.all([this.getApplicationModules(), this.getCompany(), this.getUserGroups(), this.getUserRoles()])
      .then((responses) => {
        const [applicationModulesResponse, companyResponse, userGroupsResponse, userRolesResponse] = responses;
        this.applicationModules = applicationModulesResponse.data.data;
        this.company = companyResponse.data; // The single "data" is not a typo
        this.userGroups = userGroupsResponse.data.data;
        this.userRoles = userRolesResponse.data.data;
      }).catch((error) => {
        const message: string = error.response?.data?.message ?? error.message;
        this.$bvModal.msgBoxOk(message, {
          title: 'Error Getting Data',
          okVariant: 'danger',
          centered: true,
        });
      });
  }

  private async getApplicationModules(): Promise<AxiosResponse<any>> {
    const url: string = `${process.env.VUE_APP_BACKEND_API_HOST}/applicationmodules/dashboard?sort=name`;
    const accessToken: string = await (this as any).$auth0.getAccessTokenSilently();
    return axios.get(url, {
      headers: {
        Authorization: `Bearer ${accessToken}`,
      },
    });
  }

  private async getCompany(): Promise<AxiosResponse<any>> {
    const appMetadata = (this as any).$auth0.user[process.env.VUE_APP_AUTH0_CUSTOM_CLAIM_NAMESPACE + '/app_metadata'];
    const companyId: string = appMetadata['support_access_company_id'] ?? appMetadata['company_id'];
    const url: string = `${process.env.VUE_APP_BACKEND_API_HOST}/companies/${companyId}`;
    const accessToken: string = await (this as any).$auth0.getAccessTokenSilently();
    return axios.get(url, {
      headers: {
        Authorization: `Bearer ${accessToken}`,
      },
    });
  }

  private async getUserGroups(): Promise<AxiosResponse<any>> {
    const url: string = `${process.env.VUE_APP_BACKEND_API_HOST}/usergroups/`;
    const accessToken: string = await (this as any).$auth0.getAccessTokenSilently();
    return axios.get(url, {
      headers: {
        Authorization: `Bearer ${accessToken}`,
      },
    });
  }

  private async getUserRoles(): Promise<AxiosResponse<any>> {
    const url: string = `${process.env.VUE_APP_BACKEND_API_HOST}/user-roles/`;
    const accessToken: string = await (this as any).$auth0.getAccessTokenSilently();
    return axios.get(url, {
      headers: {
        Authorization: `Bearer ${accessToken}`,
      },
    });
  }

  private get disableDownloadUserSyncToolButton(): boolean {
    if (this.applicationModules.length === 0) {
      // The application modules haven't been fetched yet
      return true;
    }
    if (this.company === null) {
      // The company info hasn't been fetched yet
      return true;
    }
    // Disable the button for users who only have the TracePhishing module and are using the free trial
    return this.company.tracephishingFreeTrial
      && this.applicationModules.length === 1
      && this.applicationModules[0].name === ApplicationModuleName.TracePhishing;
  }

  private showCreateUserModal(): void {
    this.userToEdit = null;
    this.$bvModal.show('create-edit-user-modal');
  }
  private showEditUserModal(user: User): void {
    this.userToEdit = user;
    this.$bvModal.show('create-edit-user-modal');
  }

  private refreshUserTable(): void {
    (this.$refs.userTable as any).refresh();
  }

  private confirmDeactivateUser(user: Auth0User): void {
    this.$bvModal.msgBoxConfirm(`Please confirm that you want to deactivate the user: ${user.email}`, {
      title: 'Deactivate User',
      okVariant: 'warning',
      okTitle: 'Deactivate',
      centered: true,
      autoFocusButton: 'cancel',
      noCloseOnBackdrop: true,
    }).then((deactivateUser: boolean) => {
      if (deactivateUser) {
        this.deactivateUser(user);
      }
    });
  }

  private confirmReactivateUser(user: Auth0User): void {
    this.$bvModal.msgBoxConfirm(`Please confirm that you want to reactivate the user: ${user.email}`, {
      title: 'Reactivate User',
      okVariant: 'warning',
      okTitle: 'Reactivate',
      centered: true,
      autoFocusButton: 'cancel',
      noCloseOnBackdrop: true,
    }).then((reactivateUser: boolean) => {
      if (reactivateUser) {
        this.reactivateUser(user);
      }
    });
  }

  private confirmDeleteUser(user: Auth0User): void {
    this.$bvModal.msgBoxConfirm(`Please confirm that you want to delete the user: ${user.email}`, {
      title: 'Delete User',
      okVariant: 'danger',
      okTitle: 'Delete',
      centered: true,
      autoFocusButton: 'cancel',
      noCloseOnBackdrop: true,
    }).then((deleteUser: boolean) => {
      if (deleteUser) {
        this.deleteUser(user);
      }
    });
  }

  private async deactivateUser(user: Auth0User): Promise<void> {
    const url: string = `${process.env.VUE_APP_BACKEND_API_HOST}/new_users/${user.user_id}`;
    const data = {
      isActive: false,
    };
    const accessToken: string = await (this as any).$auth0.getAccessTokenSilently();
    axios.put(url, data, {
      headers: {
        Authorization: `Bearer ${accessToken}`,
      },
    }).then((response) => {
      const message: string = `Successfully deactivated user: ${user.email}`;
      this.$bvModal.msgBoxOk(message, {
        title: 'User Deactivated',
        okVariant: 'success',
        centered: true,
      }).then(() => {
        this.refreshUserTable();
      });
    }).catch((error) => {
      const message: string = error.response?.data?.message ?? error.message;
      this.$bvModal.msgBoxOk(message, {
        title: 'Error Deactivating User',
        okVariant: 'danger',
        centered: true,
      }).then(() => {
        this.refreshUserTable();
      });
    });
  }

  private async reactivateUser(user: Auth0User): Promise<void> {
    const url: string = `${process.env.VUE_APP_BACKEND_API_HOST}/new_users/${user.user_id}`;
    const data = {
      isActive: true,
    };
    const accessToken: string = await (this as any).$auth0.getAccessTokenSilently();
    axios.put(url, data, {
      headers: {
        Authorization: `Bearer ${accessToken}`,
      },
    }).then((response) => {
      const message: string = `Successfully reactivated user: ${user.email}`;
      this.$bvModal.msgBoxOk(message, {
        title: 'User Reactivated',
        okVariant: 'success',
        centered: true,
      }).then(() => {
        this.refreshUserTable();
      });
    }).catch((error) => {
      const message: string = error.response?.data?.message ?? error.message;
      this.$bvModal.msgBoxOk(message, {
        title: 'Error Reactivating User',
        okVariant: 'danger',
        centered: true,
      }).then(() => {
        this.refreshUserTable();
      });
    });
  }

  private async deleteUser(user: Auth0User): Promise<void> {
    const url: string = `${process.env.VUE_APP_BACKEND_API_HOST}/new_users/${user.user_id}`;
    const accessToken: string = await (this as any).$auth0.getAccessTokenSilently();
    axios.delete(url, {
      headers: {
        Authorization: `Bearer ${accessToken}`,
      },
    }).then((response) => {
      const message: string = `Successfully deleted user: ${user.email}`;
      this.$bvModal.msgBoxOk(message, {
        title: 'User Deleted',
        okVariant: 'success',
        centered: true,
      }).then(() => {
        this.refreshUserTable();
      });
    }).catch((error) => {
      const message: string = error.response?.data?.message ?? error.message;
      this.$bvModal.msgBoxOk(message, {
        title: 'Error Deleting User',
        okVariant: 'danger',
        centered: true,
      }).then(() => {
        this.refreshUserTable();
      });
    });
  }

  private async changePrimaryAdmin(newPrimaryAdmin: Auth0User): Promise<void> {
    const url: string = `${process.env.VUE_APP_BACKEND_API_HOST}/new_users/change_primary_admin/${newPrimaryAdmin.user_id}`;
    const accessToken: string = await (this as any).$auth0.getAccessTokenSilently();
    axios.put(url, {}, {
      headers: {
        Authorization: `Bearer ${accessToken}`,
      },
    }).then((response) => {
      const message: string = 'Successfully changed primary admin';
      this.$bvModal.msgBoxOk(message, {
        title: 'Changed Primary Admin',
        okVariant: 'success',
        centered: true,
      }).then(() => {
        this.refreshUserTable();
      });
    }).catch((error) => {
      const message: string = error.response?.data?.message ?? error.message;
      this.$bvModal.msgBoxOk(message, {
        title: 'Error Changing Primary Admin',
        okVariant: 'danger',
        centered: true,
      }).then(() => {
        this.refreshUserTable();
      });
    });
  }

  private get mfaButtonText(): string {
    if (this.company === null || !this.company.requireMfa) {
      return 'Enable Multi-factor Authentication';
    } else {
      return 'Disable Multi-factor Authentication';
    }
  }

  private showMfaModal(): void {
    this.$bvModal.show('mfa-modal');
  }

  private get mfaModalTitle(): string {
    if (this.company === null) {
      return '';
    }
    if (this.company.requireMfa) {
      return 'Disable Multi-factor Authentication';
    } else {
      return 'Enable Multi-factor Authentication';
    }
  }

  private get mfaModalOkTitle(): string {
    if (this.company === null) {
      return '';
    }
    if (this.company.requireMfa) {
      if (this.submitting) {
        return 'Disabling...';
      } else {
        return 'Disable';
      }
    } else {
      if (this.submitting) {
        return 'Enabling...';
      } else {
        return 'Enable';
      }
    }
  }

  private async handleOkForMfaModal(bvModalEvent: Event): Promise<void> {
    bvModalEvent.preventDefault();
    this.submitting = true;
    const enablingMfa: boolean = !(this.company?.requireMfa);
    let url: string;
    if (enablingMfa) {
      url = `${process.env.VUE_APP_BACKEND_API_HOST}/new_users/enable_mfa/${this.company!.id}`;
    } else {
      url = `${process.env.VUE_APP_BACKEND_API_HOST}/new_users/disable_mfa/${this.company!.id}`;
    }
    const accessToken: string = await (this as any).$auth0.getAccessTokenSilently();
    axios.put(url, {}, {
      headers: {
        Authorization: `Bearer ${accessToken}`,
      },
    }).then((response) => {
      (this.$refs.mfaModal as any).hide();
      let message: string;
      if (enablingMfa) {
        message = 'Successfully enabled multi-factor authentication';
      } else {
        message = 'Successfully disabled multi-factor authentication';
      }
      this.$bvModal.msgBoxOk(message, {
        title: enablingMfa ? 'Enabled MFA' : 'Disabled MFA',
        okVariant: 'success',
        centered: true,
      });
    }).catch((error) => {
      const message: string = error.response?.data?.message ?? error.message;
      this.$bvModal.msgBoxOk(message, {
        title: enablingMfa ? 'Error Enabling MFA' : 'Error Disabling MFA',
        okVariant: 'danger',
        centered: true,
      });
    }).finally(() => {
      this.getCompany()
        .then((response) => {
          this.company = response.data;
        }).finally(() => {
          this.submitting = false;
          this.refreshUserTable();
        });
    });
  }
}
